<script setup lang="ts">
import type { Ref } from "vue"
import VueDatePicker from "@vuepic/vue-datepicker"
import { useField } from "vee-validate"
import { ref, toRef } from "vue"
import { Button, Hint, Icon } from "@/components/ui"
import { useTranslation } from "@/composables"
import { datepickerLocales } from "@/i18n/constants"
import "@vuepic/vue-datepicker/dist/main.css"

const props = withDefaults(defineProps<DatepickerProps>(), {
  modelValue: undefined,
  label: "",
  placeholder: "",
  disabled: false,
  format: "",
  error: "",
  hint: "",
  clearable: true,
  enableTimePicker: false,
  timezone: undefined,
})

const emit = defineEmits(["update:modelValue", "change"])

interface DatepickerProps {
  modelValue?: Date
  minDate?: Date
  name: string
  label?: string
  placeholder?: string
  disabled?: boolean
  format?: string
  error?: string
  hint?: string
  clearable?: boolean
  enableTimePicker?: boolean
  timezone?: string
}

const { t, locale: currentLanguage } = useTranslation()

const isCalendarOpen: Ref<boolean> = ref<boolean>(false)
const picker: Ref<typeof VueDatePicker | null> = ref(null)

const name = toRef(props, "name")

const { value, errorMessage, handleBlur, handleChange } = useField(name, undefined, {
  initialValue: props.modelValue,
})

function onInput(value: Date) {
  handleChange(value)
  emit("update:modelValue", value)
  emit("change", value)
}

function onClear() {
  handleChange(undefined)
  emit("update:modelValue", undefined)
}

function togglePicker() {
  if (!picker.value?.active)
    picker.value?.toggleMenu()
}

function handleMouseDown(event: MouseEvent) {
  event.preventDefault()
  event.stopPropagation()
}
</script>

<template>
  <div>
    <div
      class="gpx-input-container"
      :class="{
        'gpx-input-container_error': error || errorMessage,
      }"
    >
      <label class="gpx-input-label-container" :for="name">
        <VueDatePicker
          :id="name"
          ref="picker"
          :name="name"
          :model-value="modelValue ?? value"
          :auto-apply="!enableTimePicker"
          hide-input-icon
          placeholder=""
          class="gpx-input"
          :class="{ 'gpx-input_disabled': disabled }"
          :disabled="disabled"
          :clearable="clearable"
          :min-date="minDate"
          :timezone="timezone"
          :enable-time-picker="enableTimePicker"
          :time-picker-inline="enableTimePicker"
          :is-24="false"
          :format="props.enableTimePicker ? 'PP p' : 'PP'"
          :format-locale="datepickerLocales[currentLanguage]"
          :now-button-label="t('now')"
          :select-text="t('select')"
          :cancel-text="t('cancel')"
          @mousedown.prevent="handleMouseDown"
          @focus="togglePicker"
          @update:model-value="onInput"
          @input="handleChange"
          @blur="handleBlur"
          @cleared="onClear"
          @open="() => (isCalendarOpen = true)"
          @closed="() => (isCalendarOpen = false)"
        >
          <template #clear-icon="{ clear }">
            <Button variant="flat" @click="clear">
              <span class="sr-only">Clear input field {{ label }}</span>
              <Icon icon="Close" size="sm" />
            </Button>
          </template>
        </VueDatePicker>
        <span
          class="gpx-floating-label"
          :class="{
            'gpx-floating-label_is-floating': value,
            'gpx-floating-label_error': errorMessage || error,
            'text-gray-700': isCalendarOpen,
          }"
        >
          <Icon icon="Calendar" />
          {{ label }}
        </span>
      </label>
    </div>
    <span v-if="errorMessage || error" class="gpx-field-error" v-text="error || errorMessage" />
    <Hint v-if="hint" :hint="hint" class="mt-1.5" />
  </div>
</template>

<style scoped>
.gpx-input {
  padding: 0;
}

.gpx-floating-label {
  display: flex;
  gap: 0.5rem;
  justify-content: center;
  align-items: center;
  pointer-events: none;
}

.gpx-input:focus + .gpx-floating-label,
.gpx-floating-label_is-floating,
.gpx-input-open {
  transform: translateY(-85%) scale(0.75);
}
</style>

<style>
.gpx-input .dp__input {
  border: none;
  background-color: transparent;
  height: 3.5rem;
  padding-left: 1rem;
  padding-top: 1.5rem;
  font-family: "Open Sans", Montserrat, sans-serif;
}
</style>
